






























































import { defineComponent, ref, watchEffect, computed, useContext, provide } from '@nuxtjs/composition-api'
import { useProductStore } from '~/store/product'
import { usePdpStore } from '~/store/pdp'
import { useCMSGlobalSettingsStore } from '~/store/cms'

import PriceBrickHeader from '~/components/feature/price-brick/PriceBrickHeader.vue'
import PriceBrickContent from '~/components/feature/price-brick/PriceBrickContent.vue'
import OldPriceBrickContent from '~/components/feature/price-brick/OldPriceBrickContent.vue'
import PDPPriceBrickSkeleton from '~/components/global/skeletons/PDPPriceBrickSkeleton.vue'

import { usePriceBrick } from '~/composables/price-brick/usePriceBrick'
import useEcommerceAnalytics from '@/composables/analytics/useEcommerceAnalytics'
import { useMobile } from '~/composables/ui/useMobile'
import unavailableProducts from '@/static/unavailableProducts.json'
import { useCountry } from '~/composables/country/useCountry'
import { PRODUCT_STATUS } from '~/common/utils/product/constants'
import { getProductStatus } from '~/common/utils/product/product'
import { useStickyPdpComponent } from '~/composables/ui/useStickyPdpComponent'

export default defineComponent({
  components: { PriceBrickHeader, PriceBrickContent, OldPriceBrickContent, PDPPriceBrickSkeleton },
  setup() {
    const { trackAddToCart, trackAddToOldPriceBrick } = useEcommerceAnalytics()
    const { isForbiddenToSell, showOldPriceBrick, showInventory, mapSkuData, getCustomFormulationLabel } =
      usePriceBrick()
    const { isJapan, isCN } = useCountry()
    const productStore = useProductStore()
    const { template } = usePdpStore()
    const { getAttributeByCountry } = useCMSGlobalSettingsStore()
    const currentProduct = computed(() => productStore.currentProduct)
    const ctx = useContext()
    const isPreviewServer = ctx.$config.cst.isPreview
    const customFormulationLabel = computed(() => {
      return getCustomFormulationLabel(currentProduct.value.data.productCategory, true)
    })

    const hidePriceBrickForCurrentProduct = getAttributeByCountry('hidePriceBrick')?.value.includes(
      currentProduct.value.data.key
    )
    const currentProductStatus = computed(() => getProductStatus(currentProduct.value))
    const skuData = computed(() => mapSkuData(Object.values(currentProduct.value.data.skuData)))
    const showComponent = computed(
      () =>
        currentProductStatus.value !== PRODUCT_STATUS.DISCONTINUED &&
        (currentProductStatus.value !== PRODUCT_STATUS.PENDING || isPreviewServer) &&
        !hidePriceBrickForCurrentProduct
    )
    const isPreReleased = computed(() => currentProductStatus.value === PRODUCT_STATUS.PRE_RELEASED)
    const isPending = computed(() => currentProductStatus.value === PRODUCT_STATUS.PENDING)
    const isCNDangerousProduct = computed(() => currentProduct.value.data.attributes.isCNDangerousGoods && isCN.value)

    const productId = computed(() => currentProduct.value.data.key)
    const productName = computed(() => currentProduct.value.data.name)
    const productType = computed(() => currentProduct.value.data.productType)

    const addAsteriskWhenDangerousProduct = (sku: string) => {
      return `${sku}${isCNDangerousProduct.value ? '**' : ''}`
    }

    const headerText = ref(
      addAsteriskWhenDangerousProduct(
        (showOldPriceBrick.value ? skuData.value[0]?.sku : productId.value) ?? productId.value
      )
    )
    const { isMobile } = useMobile()
    const isPriceBrickOpened = ref(true)

    const { stickyStyle, wrapperRef, isSticky } = useStickyPdpComponent()

    const isJpDeleteriousProduct = computed(
      () => isJapan.value && unavailableProducts.japanDeleteriousProducts.includes(currentProduct.value.data.key)
    )

    const isJpPoisonousProduct = computed(
      () => isJapan.value && unavailableProducts.japanPoisonousProducts.includes(currentProduct.value.data.key)
    )

    const sectionClass = computed(() => {
      let classes = 'tw-z-10'

      if (isMobile.value && isSticky.value) {
        classes += ' tw-left-0 tw-right-0 !tw-w-full'
      }

      if (isPreviewServer && (isPreReleased.value || isPending.value)) {
        classes += ' !tw-border-0 !tw-shadow-none'
      }

      return classes
    })

    watchEffect(() => {
      isPriceBrickOpened.value = !isMobile.value || !isSticky.value
    })

    const onAddToBasket = (params: { items: Array<any>; trackAsOldPriceBrick: boolean }) => {
      params.trackAsOldPriceBrick
        ? trackAddToOldPriceBrick('Product Detail Page', params.items)
        : trackAddToCart('Product Detail Page', params.items)
    }

    provide('currentProduct', currentProduct.value.data)

    return {
      isForbiddenToSell,
      productId,
      productName,
      productType,
      skuData,
      onAddToBasket,
      showOldPriceBrick,
      showInventory,
      wrapperRef,
      stickyStyle,
      isMobile,
      isPriceBrickOpened,
      isSticky,
      isJpDeleteriousProduct,
      isJpPoisonousProduct,
      showComponent,
      customFormulationLabel,
      headerText,
      template,
      isPreviewServer,
      sectionClass,
      isPreReleased,
      isPending,
      isCNDangerousProduct,
      addAsteriskWhenDangerousProduct,
    }
  },
})
